import React, { useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFadeIn } from "../App";
import SafeVideo from "../components/SafeVideo";
import ShapeDivider from "../components/ShapeDivider";
import ImageModal from "../components/ImageModal";
import { pageImageAllocations, getImageById } from "../data/wildlifeGallery";

const WhatWeDo = () => {
  const [heroRef, heroVisible] = useFadeIn();
  const [modelRef, modelVisible] = useFadeIn();
  const [missionRef, missionVisible] = useFadeIn();
  const [pillarsRef, pillarsVisible] = useFadeIn();
  const [galleryRef, galleryVisible] = useFadeIn();
  const [communityRef, communityVisible] = useFadeIn();
  const [videosRef, videosVisible] = useFadeIn();
  const [ctaRef, ctaVisible] = useFadeIn();

  // Modal state for image viewing
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState({ url: '', alt: '' });

  const openImageModal = (imageUrl, imageAlt) => {
    setModalImage({ url: imageUrl, alt: imageAlt });
    setShowModal(true);
  };

  const pillars = [
    {
      icon: "fa-heart-pulse",
      title: "Care",
      description:
        "We establish wildlife care hubs, run by licenced carers and supported by passionate volunteers, to ensure the well-being and future of Australian wildlife. These hubs provide centralised care, facilities, and transportation for successful rehabilitation and release.",
      imageId: pageImageAllocations.whatWeDo.pillars[0],
    },
    {
      icon: "fa-seedling",
      title: "Environment",
      description:
        "We collaborate with organisations to support released wildlife by addressing habitat, food, and community awareness. Through research, habitat restoration, and community engagement, we ensure the well-being of animals in their release sites.",
      imageId: pageImageAllocations.whatWeDo.pillars[1],
    },
    {
      icon: "fa-leaf",
      title: "Natural Foods",
      description:
        "Our research project aims to develop automated systems for natural food sources, reducing reliance on retail pet food and promoting sustainability for carers. This fosters an interconnected living system, benefiting animal well-being and the environment.",
      imageId: pageImageAllocations.whatWeDo.pillars[2],
    },
    {
      icon: "fa-users",
      title: "Buddies",
      description:
        "Our project aims to develop a system and applications that enable carers to connect with their community for support, both locally and on a broader scale. This interconnected network promotes the well-being of animals and carers, facilitating communication, collaboration, and resource sharing.",
      imageId: pageImageAllocations.whatWeDo.pillars[3],
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`hero-whatwedo-section fade-in-section ${heroVisible ? "is-visible" : ""}`}
        style={{
          backgroundImage: `linear-gradient(rgba(141, 191, 75, 0.8), rgba(74, 92, 45, 0.8)), url(${getImageById('galah').url})`,
          padding: "120px 0 80px",
          color: "white",
          position: "relative",
          overflow: "hidden",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <div
          className="hero-pattern"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Cpath d="M30 10c11.046 0 20 8.954 20 20s-8.954 20-20 20S10 41.046 10 30s8.954-20 20-20zm0 2c-9.941 0-18 8.059-18 18s8.059 18 18 18 18-8.059 18-18-8.059-18-18-18z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          }}
        ></div>
        <Container className="position-relative">
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <h1 className="display-3 fw-bold mb-4" style={{ color: "white" }}>
                What We Do
              </h1>
              <p
                className="lead mb-5"
                style={{ fontSize: "1.3rem", opacity: 0.95 }}
              >
                Promoting sustainable and ethical rehabilitation practices
                through our comprehensive model
              </p>
            </Col>
          </Row>
        </Container>
        <ShapeDivider color="white" />
      </section>

      {/* Our Model Section */}
      <section
        ref={modelRef}
        className={`model-section fade-in-section ${modelVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={10}>
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Model
              </h2>
              <p
                className="lead mb-4"
                style={{ fontSize: "1.2rem", lineHeight: "1.8" }}
              >
                Our key objective is to promote sustainable and ethical
                rehabilitation practices by providing care, a suitable
                environment, natural foods, and companionship.
              </p>
              <p
                style={{
                  fontSize: "1.1rem",
                  lineHeight: "1.7",
                  color: "var(--dark-text)",
                }}
              >
                Additionally, we aim to educate and advocate for a compassionate
                and interconnected system that benefits all living beings.
              </p>
            </Col>
          </Row>

          <Row className="align-items-center g-5">
            <Col lg={6}>
              <div className="model-image-container">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F05521795175141ceae3328bbe48d16d5?format=webp&width=800"
                  alt="Australian Magpie - intelligent native bird with striking orange eyes"
                  className="img-fluid rounded-3 shadow-lg"
                  style={{ width: "100%", height: "400px", objectFit: "cover" }}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="model-features">
                <div
                  className="feature-item mb-4 p-3 rounded-3"
                  style={{ background: "var(--light-bg)" }}
                >
                  <h5
                    className="fw-bold mb-2"
                    style={{ color: "var(--primary-green)" }}
                  >
                    <i className="fas fa-target me-2"></i>
                    Conservation Focus
                  </h5>
                  <p className="mb-0">
                    Conserving the natural environment through sustainable
                    wildlife rehabilitation and release
                  </p>
                </div>
                <div
                  className="feature-item mb-4 p-3 rounded-3"
                  style={{ background: "var(--light-bg)" }}
                >
                  <h5
                    className="fw-bold mb-2"
                    style={{ color: "var(--primary-green)" }}
                  >
                    <i className="fas fa-graduation-cap me-2"></i>
                    Education & Awareness
                  </h5>
                  <p className="mb-0">
                    Raising awareness about wildlife issues and supporting
                    conservation efforts
                  </p>
                </div>
                <div
                  className="feature-item p-3 rounded-3"
                  style={{ background: "var(--light-bg)" }}
                >
                  <h5
                    className="fw-bold mb-2"
                    style={{ color: "var(--primary-green)" }}
                  >
                    <i className="fas fa-leaf me-2"></i>
                    Sustainable Systems
                  </h5>
                  <p className="mb-0">
                    Developing and maintaining systems for natural food sources
                    and advocating sustainability
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission Statement Section */}
      <section
        ref={missionRef}
        className={`mission-statement-section section-bg fade-in-section ${missionVisible ? "is-visible" : ""}`}
        style={{ padding: "80px 0" }}
      >
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <div
                className="mission-card p-5 rounded-3 shadow-lg"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              >
                <h3
                  className="mb-4"
                  style={{ color: "var(--primary-green)", fontSize: "2rem" }}
                >
                  Our Mission
                </h3>
                <p
                  className="lead"
                  style={{
                    fontSize: "1.2rem",
                    lineHeight: "1.8",
                    color: "var(--dark-text)",
                  }}
                >
                  Our mission is to conserve the natural environment through
                  sustainable wildlife rehabilitation and release. We improve
                  welfare outcomes, raise awareness about wildlife issues, and
                  support conservation efforts. By developing and maintaining
                  systems for natural food sources, sharing knowledge, and
                  advocating sustainability, we ensure the well-being of animals
                  in our care.
                </p>
                <div className="mt-4 text-center">
                  <span
                    className="badge px-4 py-2"
                    style={{
                      background: "var(--primary-yellow)",
                      color: "white",
                      fontSize: "1rem",
                      borderRadius: "50px",
                    }}
                  >
                    All this is based on our 4 focal pillars!
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 4 Pillars Section */}
      <section
        ref={pillarsRef}
        className={`pillars-section fade-in-section ${pillarsVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our 4 Focal Pillars
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Each pillar represents a crucial component of our holistic
                approach to wildlife rehabilitation
              </p>
            </Col>
          </Row>

          <Row className="g-5">
            {pillars.map((pillar, index) => {
              const pillarImage = getImageById(pillar.imageId);
              return (
                <Col md={6} key={index}>
                  <Card
                    className="h-100 border-0 shadow-lg pillar-card-detailed"
                    style={{
                      borderRadius: "20px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                  >
                    <div
                      className="card-image-container position-relative"
                      style={{ height: "250px", overflow: "hidden" }}
                    >
                      <img
                        src={pillarImage.url}
                        alt={pillarImage.alt}
                        className="card-img-top"
                        style={{
                          height: "100%",
                          width: "100%",
                          objectFit: "cover",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      <div
                        className="card-overlay position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(135deg, rgba(74, 92, 45, 0.8) 0%, rgba(141, 191, 75, 0.8) 100%)",
                        }}
                      >
                        <div
                          className="pillar-icon"
                          style={{
                            width: "80px",
                            height: "80px",
                            background: "rgba(255, 255, 255, 0.2)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "2rem",
                            border: "2px solid rgba(255, 255, 255, 0.3)",
                          }}
                        >
                          <i className={`fas ${pillar.icon}`}></i>
                        </div>
                      </div>
                    </div>
                    <Card.Body className="p-4">
                      <h4
                        className="fw-bold mb-3"
                        style={{ color: "var(--primary-green)" }}
                      >
                        {pillar.title}
                      </h4>
                      <p className="text-muted" style={{ lineHeight: "1.7" }}>
                        {pillar.description}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Wildlife Gallery Section */}
      <section
        ref={galleryRef}
        className={`wildlife-gallery-whatwedo fade-in-section ${galleryVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "var(--light-bg)" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Rehabilitation in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                See our 4-pillar approach in practice with real rehabilitation cases
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {pageImageAllocations.whatWeDo.gallery.map((imageId, index) => {
              const image = getImageById(imageId);
              return (
                <Col md={6} lg={3} key={image.id}>
                  <div
                    className="wildlife-gallery-card shadow"
                    style={{
                      borderRadius: "15px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-100"
                      style={{
                        height: "200px",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        cursor: "pointer"
                      }}
                      onClick={() => openImageModal(image.url, image.alt)}
                    />
                  </div>
                </Col>
              );
            })}
            {[
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F3a34dce3d09148cebe42b83b2c1c9a84?format=webp&width=2560&quality=95",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F5f49da0377ef4f179273f359faa4f8c5?format=webp&width=2560&quality=95",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Faeb9ee5644044c19bf5c41e68cc892b6?format=webp&width=2560&quality=95"
            ].map((imageUrl, index) => (
              <Col md={6} lg={3} key={`new-image-${index}`}>
                <div
                  className="wildlife-gallery-card shadow"
                  style={{
                    borderRadius: "15px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Wildlife Rehabilitation"
                    className="w-100"
                    style={{
                      height: "200px",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                      cursor: "pointer"
                    }}
                    onClick={() => openImageModal(imageUrl, "Wildlife Rehabilitation")}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Community Impact - Real Photos */}
      <section
        ref={communityRef}
        className={`community-impact-section fade-in-section ${communityVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "white" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Community in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                See the real faces and moments behind our conservation work - from community education events to hands-on wildlife rehabilitation
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F16c597da1cc9463b874437e7c44b4802?format=webp&width=2560&quality=95",
                title: "Family Community Events",
                description: "Engaging families and children in wildlife education through fun, interactive community events that build awareness and connection with native Australian wildlife."
              },
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F05093019677f420c9c39c02d5fcc659b?format=webp&width=2560&quality=95",
                title: "Hands-On Rehabilitation",
                description: "Our experienced carers provide specialised care for injured and orphaned wildlife, giving each animal the best chance for a successful return to the wild."
              },
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd3e70ada83c54d7397f8cfed1dc39bde?format=webp&width=2560&quality=95",
                title: "Professional Care Team",
                description: "Our dedicated team members work tirelessly to provide round-the-clock care, ensuring every animal receives the attention and specialised treatment they need."
              },
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F2ecefffba42444368cdd2e0ce1c157bc?format=webp&width=2560&quality=95",
                title: "Community Fundraising",
                description: "Through grassroots fundraising efforts and community support, we gather the resources needed to continue our vital conservation work across Queensland."
              },
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fddbf051e3e114cc790630656fe078916?format=webp&width=2560&quality=95",
                title: "Wildlife Facilities",
                description: "Behind the scenes at our rehabilitation facilities, where we maintain specialised housing and care environments for different species during their recovery journey."
              },
              {
                image: "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fdb555bd4326d4dba9e40199da191b1a2?format=webp&width=2560&quality=95",
                title: "Heartwarming Moments",
                description: "The joy and fulfillment that comes from successful rehabilitation - building bonds with the wildlife we care for and celebrating each recovery milestone."
              }
            ].map((item, index) => (
              <Col md={6} lg={4} key={index}>
                <Card
                  className="community-impact-card h-100 border-0 shadow-lg"
                  style={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <div
                    className="card-image-container"
                    style={{ height: "250px", overflow: "hidden" }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="card-img-top"
                      style={{
                        height: "100%",
                        width: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        cursor: "pointer"
                      }}
                      onClick={() => openImageModal(item.image, item.title)}
                    />
                  </div>
                  <Card.Body className="p-4">
                    <h5
                      className="fw-bold mb-3"
                      style={{ color: "var(--primary-green)" }}
                    >
                      {item.title}
                    </h5>
                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                    >
                      {item.description}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Rehabilitation in Action Videos */}
      <section
        ref={videosRef}
        className={`rehabilitation-videos-section fade-in-section ${videosVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "var(--light-bg)" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Rehabilitation in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                See our 4-pillar approach come to life through real rehabilitation work -
                from initial care to successful recovery, every step is guided by our ethical principles
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {[
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fbe9624a851954988bbff987f1192a9d3?alt=media&token=6725d2b3-4061-45a1-802c-36fa3950ed5c&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fdea29857cefc40ce8ac9b7ebe34d2250?alt=media&token=e465e438-e72e-42fb-8d96-6444cce3215c&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd90c13b465db4be9b825be5c5e78fa74?alt=media&token=9a0b1323-5308-4587-907b-976c3111c8ec&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fda85e00e0621459db464fa86ffb1e7be?alt=media&token=339e04f0-58f1-44f5-868e-85f296f6a061&apiKey=35ffb80beae24a4483ace4ab1c92b206"
            ].map((videoUrl, index) => (
              <Col md={6} lg={3} key={index}>
                <div
                  className="video-container shadow-lg"
                  style={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <SafeVideo
                    src={videoUrl}
                    controls
                    className="w-100"
                    style={{ aspectRatio: "16/9", objectFit: "cover" }}
                    preload="metadata"
                    autoGeneratePoster={true}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Call to Action Section */}
      <section
        ref={ctaRef}
        className={`cta-whatwedo-section wildlife-bg-section fade-in-section ${ctaVisible ? "is-visible" : ""}`}
        style={{
          backgroundImage: `linear-gradient(rgba(74, 92, 45, 0.85), rgba(141, 191, 75, 0.85)), url(${getImageById('kookaburra-portrait').url})`,
          padding: "80px 0",
          color: "white",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <Container className="text-center">
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2
                className="mb-4"
                style={{ color: "white", fontSize: "2.5rem" }}
              >
                Ready to Learn More?
              </h2>
              <p
                className="lead mb-4"
                style={{ fontSize: "1.2rem", opacity: 0.9 }}
              >
                Discover how you can be part of our mission to create a
                sustainable future for Australia's wildlife
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  as={Link}
                  to="/team"
                  variant="light"
                  size="lg"
                  className="px-4"
                  style={{ borderRadius: "50px", fontWeight: "600" }}
                >
                  Meet Our Team
                </Button>
                <Button
                  as={Link}
                  to="/contact"
                  variant="outline-light"
                  size="lg"
                  className="px-4"
                  style={{ borderRadius: "50px", fontWeight: "600" }}
                >
                  Get Involved
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Image Modal */}
      <ImageModal
        show={showModal}
        onHide={() => setShowModal(false)}
        imageUrl={modalImage.url}
        imageAlt={modalImage.alt}
      />
    </>
  );
};

export default WhatWeDo;
